import { gsap } from 'gsap'

const initMegaMenu = (options) => {
  const megaMenuLinks = [...document.querySelectorAll('.nav--main-nav .nav__link--parent')]
  const megaMenuBackdrop = document.getElementById('header-nav-backdrop')
  const navBackground = document.querySelector('.header__nav-background')
  const header = document.querySelector('.header')
  const headerOuter = document.querySelector('.header__outer')

  const BG_IN_DURATION = 0.5
  const BG_OUT_DURATION = 0.55
  const FROST_DELAY = 0.1

  // ── Background animation ──────────────────────────────────────────────────
  // Incremented whenever a new menu opens, invalidating any in-flight bg-close
  // callbacks that were triggered by a previous animateChildItemsOut call.
  let closeGeneration = 0

  const frostHeader = () => header?.classList.add('header--menu-open')
  const unfrostHeader = () => header?.classList.remove('header--menu-open')

  const getHeaderBottom = () =>
    headerOuter?.getBoundingClientRect().bottom
    ?? header?.getBoundingClientRect().bottom
    ?? 0

  // Backdrop sits outside .header — top is viewport Y, kept at the frost panel edge
  const syncBackdropTop = (panelHeight) => {
    megaMenuBackdrop?.style.setProperty(
      '--header-nav-backdrop-top',
      `${getHeaderBottom() + panelHeight}px`,
    )
  }

  const clearBackdropTop = () => {
    megaMenuBackdrop?.style.removeProperty('--header-nav-backdrop-top')
  }

  const animateNavBackgroundOut = () => {
    if (!navBackground) return
    gsap.killTweensOf(navBackground)

    gsap.to(navBackground, {
      height: 0,
      duration: BG_OUT_DURATION,
      ease: 'expo.inOut',
      onUpdate: () => {
        syncBackdropTop(parseFloat(gsap.getProperty(navBackground, 'height')) || 0)
      },
      onComplete: () => {
        // Keep display:block so the backdrop-filter layer stays warm for the next open
        gsap.set(navBackground, { height: 0 })
        unfrostHeader()
        megaMenuBackdrop?.classList.remove('header__nav-backdrop--show')
        clearBackdropTop()
      },
    })
  }

  // ── Child item animations ─────────────────────────────────────────────────

  const animateChildItems = menuLink => {
    const childWrap = menuLink.parentElement?.querySelector('.nav__wrap--child')
    if (!childWrap) return

    const items = [...childWrap.querySelectorAll('.nav__item--child')]
    if (!items.length) return

    // Invalidate any pending bg-close scheduled by a previous close animation
    closeGeneration++

    gsap.killTweensOf(items)
    gsap.killTweensOf(navBackground)

    const currentHeight = navBackground
      ? parseFloat(gsap.getProperty(navBackground, 'height')) || 0
      : 0
    const isFreshOpen = currentHeight === 0

    const headerBottom = getHeaderBottom()
    const targetHeight = Math.max(0, childWrap.getBoundingClientRect().bottom - headerBottom)

    frostHeader()
    gsap.set(items, { autoAlpha: 0, y: -8 })
    syncBackdropTop(currentHeight)

    if (navBackground) {
      gsap.set(navBackground, {
        display: 'block',
        height: currentHeight,
      })
    }

    const revealItem = (item, position = 0) => {
      gsap.to(item, {
        autoAlpha: 1,
        y: 0,
        duration: 0.4,
        ease: 'sine.out',
        delay: position,
        clearProps: 'opacity,visibility,transform',
      })
    }

    const syncPanel = () => {
      if (!navBackground) return 0
      const h = parseFloat(gsap.getProperty(navBackground, 'height')) || 0
      syncBackdropTop(h)
      return h
    }

    // Menu switch: panel already covers the area — stagger items in immediately
    if (!isFreshOpen) {
      items.forEach((item, i) => revealItem(item, i * 0.05))

      if (navBackground) {
        gsap.to(navBackground, {
          height: targetHeight,
          duration: BG_IN_DURATION,
          ease: 'expo.inOut',
          onUpdate: syncPanel,
          onComplete: syncPanel,
        })
      }
      return
    }

    // Fresh open: reveal each item only once the panel has reached it
    const revealed = new Set()

    const tryReveal = () => {
      const h = syncPanel()
      const panelBottom = headerBottom + h

      items.forEach((item) => {
        if (revealed.has(item)) return
        if (panelBottom >= item.getBoundingClientRect().top) {
          revealed.add(item)
          revealItem(item)
        }
      })
    }

    if (!navBackground) {
      items.forEach((item, i) => revealItem(item, i * 0.055))
      return
    }

    gsap.to(navBackground, {
      height: targetHeight,
      duration: BG_IN_DURATION,
      delay: FROST_DELAY,
      ease: 'expo.inOut',
      onUpdate: tryReveal,
      onComplete: tryReveal,
    })
  }

  // closeBg: whether to shrink the background once all items have faded out.
  // Pass false when another menu is opening immediately after (switching menus).
  const animateChildItemsOut = (menuLink, closeBg = false) => {
    const childWrap = menuLink.parentElement?.querySelector('.nav__wrap--child')
    if (!childWrap) {
      menuLink.classList.remove('open', 'closing')
      return
    }

    const items = [...childWrap.querySelectorAll('.nav__item--child')]
    if (!items.length) {
      menuLink.classList.remove('open', 'closing')
      return
    }

    menuLink.classList.add('closing')
    gsap.killTweensOf(items)

    // Capture the current generation — if animateChildItems opens another menu
    // before onComplete, generation will have incremented and we skip the bg-close.
    const myGeneration = closeGeneration

    gsap.to(items, {
      autoAlpha: 0,
      y: -6,
      duration: 0.28,
      ease: 'sine.in',
      stagger: {
        each: 0.055,
        from: 'end',
      },
      onComplete: () => {
        menuLink.classList.remove('open', 'closing')
        if (closeBg && closeGeneration === myGeneration) {
          animateNavBackgroundOut()
        }
      },
    })
  }

  // ── Indicator ─────────────────────────────────────────────────────────────

  const navWrap = document.querySelector('.nav--main-nav .nav__wrap--parent')
  const navItems = [...document.querySelectorAll('.nav--main-nav .nav__item--parent')]

  if (navWrap) {
    navItems.forEach(item => {
      item.addEventListener('mouseenter', () => {
        if (window.innerWidth < (options?.largeWidth ?? 1200)) return
        const wrapRect = navWrap.getBoundingClientRect()
        const itemRect = item.getBoundingClientRect()
        navWrap.style.setProperty('--nav-indicator-left', `${itemRect.left - wrapRect.left}px`)
        navWrap.style.setProperty('--nav-indicator-width', `${itemRect.width}px`)
        navWrap.style.setProperty('--nav-indicator-opacity', '1')
      })

      item.addEventListener('mouseleave', (e) => {
        if (window.innerWidth < (options?.largeWidth ?? 1200)) return
        if (navWrap.contains(e.relatedTarget)) return
        navWrap.style.setProperty('--nav-indicator-opacity', '0')
      })
    })
  }

  // ── Parent item hover ─────────────────────────────────────────────────────

  ;[...document.querySelectorAll('.nav--main-nav .nav__item--parent.nav__item--has-children')].forEach(parentItem => {
    parentItem.addEventListener('mouseleave', (event) => {
      if (parentItem.contains(event.relatedTarget) || window.innerWidth < (options?.largeWidth)) return

      const menuLink = parentItem.querySelector('.nav__link--parent')
      // Backdrop stays until the frost panel finishes collapsing
      animateChildItemsOut(menuLink, true)
    })

    parentItem.addEventListener('mouseenter', () => {
      if (window.innerWidth < (options?.largeWidth)) return
      const menuLink = parentItem.querySelector('.nav__link--parent')
      menuLink.classList.add('open')
      megaMenuBackdrop.classList.toggle('header__nav-backdrop--show', true)
      animateChildItems(menuLink)
    })
  })

  // ── Touch / keyboard ──────────────────────────────────────────────────────

  const handleTouchEvent = (navItem, e) => {
    if (window.innerWidth < (options?.largeWidth ?? 1200)) return true
    if (navItem.classList.contains('open')) return true

    closeSiblings(navItem)
    e.preventDefault()
  }

  const closeSiblings = (excludedElement) => {
    if (excludedElement) {
      megaMenuBackdrop.classList.add('header__nav-backdrop--show')
    }

    megaMenuLinks.forEach(sibling => {
      if (sibling === excludedElement) {
        sibling.classList.add('open')
      } else if (sibling.classList.contains('open') || sibling.classList.contains('closing')) {
        // closeBg: only when closing everything (no new menu opening)
        animateChildItemsOut(sibling, !excludedElement)
      }
    })

    if (excludedElement) {
      animateChildItems(excludedElement)
    }
  }

  megaMenuBackdrop.addEventListener('click', () => closeSiblings())

  megaMenuLinks.forEach(menuLink => {
    menuLink.addEventListener('touchend', (event) => {
      megaMenuBackdrop.classList.toggle('header__nav-backdrop--show', true)
      handleTouchEvent(menuLink, event)
    })

    menuLink.addEventListener('keydown', (event) => {
      if (event.code === 'Space') {
        event.preventDefault()
        closeSiblings(menuLink.classList.contains('open') ? null : menuLink)
        return false
      } else if (event.code === 'Escape') {
        closeSiblings()
      }
    })
  })

  // Pre-create backdrop-filter compositing layers so the first open isn't slower
  const warmFrostLayers = () => {
    if (!navBackground) return

    gsap.set(navBackground, {
      display: 'block',
      height: 1,
      opacity: 0,
      pointerEvents: 'none',
    })
    frostHeader()
    void navBackground.offsetHeight

    requestAnimationFrame(() => {
      unfrostHeader()
      gsap.set(navBackground, {
        height: 0,
        clearProps: 'opacity,pointerEvents',
      })
    })
  }

  if (typeof requestIdleCallback === 'function') {
    requestIdleCallback(warmFrostLayers, { timeout: 1500 })
  } else {
    setTimeout(warmFrostLayers, 200)
  }
}

export default initMegaMenu
